import React, { useRef } from 'react';

import { MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material';
import type { PopperProps } from '@mui/material';
import { useClickAway } from 'react-use';
import { IconColors } from 'src/components/icons/icon-utils';
import { ManualEntryIcon } from 'src/components/icons/ManualEntryIcon';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { useTypeMenuStyles } from 'src/editor';
import { Apollo } from 'src/gql/generated';

import type { ColumnHeaderQuestion } from './ColumnHeaderMenu';

interface ExtractionModePopperProps {
  onChangeMode: (mode: Apollo.ExtractionQuestionMode) => Promise<void>;
  onClose: () => void;
  popperProps: Omit<PopperProps, 'children'>;
  question: ColumnHeaderQuestion;
}

export function ExtractionModePopper({
  onChangeMode,
  onClose,
  popperProps: { anchorEl, placement, ...rest },
  question,
}: ExtractionModePopperProps) {
  const { classes } = useTypeMenuStyles();
  const popperRef = useRef<HTMLDivElement>(null);

  useClickAway(popperRef, onClose);

  return (
    <Popper
      ref={popperRef}
      anchorEl={anchorEl}
      className={classes.popper}
      modifiers={[
        {
          name: 'offset',
          options: {
            enabled: true,
            offset: '0, 12',
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundariesElement: 'viewport',
            enabled: true,
            padding: 16,
          },
        },
      ]}
      placement={placement}
      onClick={e => e.stopPropagation()}
      {...rest}
    >
      <Paper>
        <MenuList>
          <MenuItem
            selected={question.mode === Apollo.ExtractionQuestionMode.Ai}
            style={{ alignItems: 'center', display: 'flex', gap: 8 }}
            onClick={async e => {
              e.stopPropagation();
              await onChangeMode(Apollo.ExtractionQuestionMode.Ai);
            }}
          >
            <SmallAiSparkleIcon fill={IconColors.Primary} />
            <Typography>Aline AI</Typography>
          </MenuItem>
          <MenuItem
            selected={question.mode === Apollo.ExtractionQuestionMode.Manual}
            style={{ alignItems: 'center', display: 'flex', gap: 8 }}
            onClick={async e => {
              e.stopPropagation();
              await onChangeMode(Apollo.ExtractionQuestionMode.Manual);
            }}
          >
            <ManualEntryIcon fill={IconColors.Primary} />
            <Typography>Manual Entry</Typography>
          </MenuItem>
        </MenuList>
      </Paper>
    </Popper>
  );
}
